import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { auth, db } from "../firebase";
import { deleteDoc, deleteField, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { normalizeDateInput } from "../utils/date";
import { deadlineFields, lastUpdate, manualDeadline } from "../utils/home";
import { appendStage, seedHistory } from "../utils/applicationHistory";
import type { ApiItem, Status } from "../utils/applicationCards";
import { localDate } from "../utils/newRoles";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MELT_BUTTON_QUIET, MELT_DIALOG } from "./ui/melt";
import { cn } from "../lib/utils";
import { DatePicker } from "./ui/date-picker";

// Pieces shared by the tracker's board cards and list rows.

// ---------------------------------------------------------------- columns

export type ColumnDef = {
  id: string;
  title: string;
  color: string;
  /** Stage a new application gets when added from this column. */
  addStatus: Status;
  empty: { title: string; text: React.ReactNode };
};

/** Empty-state copy for archived (read-only) cycles: nothing can land there anymore. */
const ARCHIVED_EMPTY = { title: "None in this cycle", text: "Nothing reached this stage before it was archived." };
const NO_MATCHES = { title: "No matches", text: "Nothing in this stage matches your search." };

export const COLUMNS: ColumnDef[] = [
  {
    id: "applied",
    title: "Applied",
    color: "#C9A06B",
    addStatus: "submitted",
    empty: {
      title: "No applications yet",
      text: (
        <>
          <Link to="/dashboard/roles" className="font-medium text-melt-text1 underline underline-offset-2">
            Start applying
          </Link>
          , sync your inbox, or add one with +.
        </>
      ),
    },
  },
  {
    id: "interviews",
    title: "Interviews & OA",
    color: "#D98829",
    addStatus: "interview",
    empty: { title: "No interviews yet", text: "When an interview or OA email lands, Caramel moves the card here." },
  },
  {
    id: "offer",
    title: "Offer",
    color: "#5E9C7F",
    addStatus: "offer",
    empty: { title: "No offers yet", text: "They’ll land here on their own — keep applying." },
  },
  {
    id: "rejected",
    title: "Rejected",
    color: "#C9795A",
    addStatus: "rejected",
    empty: { title: "No rejections", text: "Nothing here, and that’s a good thing." },
  },
  {
    id: "ghosted",
    title: "Ghosted",
    color: "#B5AAA0",
    addStatus: "ghosted",
    empty: { title: "Nobody’s ghosting you", text: "Applications with no reply for 60 days end up here." },
  },
];

/** What an empty column/section says: search results first, then archive vs live tracker. */
export function emptyStateFor(column: ColumnDef, { readOnly, searching }: { readOnly: boolean; searching: boolean }) {
  if (searching) return NO_MATCHES;
  return readOnly ? ARCHIVED_EMPTY : column.empty;
}

// Cards with an unrecognised label aren't shown (same as before the redesign).
export function columnFor(status: Status | null): string | null {
  if (status === "submitted") return "applied";
  if (status === "interview" || status === "oa") return "interviews";
  if (status === "offer") return "offer";
  if (status === "rejected") return "rejected";
  if (status === "ghosted") return "ghosted";
  return null;
}

/** Most recently updated first (see lastUpdate); undated last. */
export const byUpdateDesc = (a: ApiItem, b: ApiItem) =>
  (Date.parse(lastUpdate(b)) || -Infinity) - (Date.parse(lastUpdate(a)) || -Infinity);

// ---------------------------------------------------------------- tags & dates

export const STATUS_TAG: Record<Status, { label: string; className: string }> = {
  submitted: { label: "Applied", className: "bg-[#F2EDE4] text-melt-text1" },
  interview: { label: "Interview", className: "bg-melt-peach text-[#8A5210]" },
  oa: { label: "OA", className: "bg-melt-peach text-[#8A5210]" },
  offer: { label: "Offer", className: "bg-[#EAF4EF] text-[#2F6B54]" },
  rejected: { label: "Rejected", className: "bg-[#F7E6DE] text-[#9A4F33]" },
  ghosted: { label: "Ghosted", className: "bg-[#EFEBE6] text-melt-text2" },
};

export const TAG = "rounded-md px-[9px] py-[3px] text-[11px] font-medium";
export const OUTLINE_TAG = cn(TAG, "border border-melt-text1/[.14] text-melt-text2");
/** The OA / interview date on a card ("OA due Friday") */
export const DEADLINE_TAG = cn(TAG, "bg-melt-caramel/[.14] text-[#8A5A2B]");

export const EDIT_INPUT =
  "w-full rounded-lg border border-melt-hair bg-white px-2.5 py-1.5 text-[13px] text-melt-text1 outline-none placeholder:text-melt-text3 focus:border-melt-caramel";

export const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "submitted", label: "Applied" },
  { value: "interview", label: "Interview" },
  { value: "oa", label: "Online Assessment" },
  { value: "offer", label: "Offer" },
  { value: "rejected", label: "Rejected" },
  { value: "ghosted", label: "Ghosted" },
];

/** "Sep 25" this year, "Sep 25, 2025" otherwise. */
export function formatAppDate(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

// ---------------------------------------------------------------- edit / save / delete

/** Edit fields plus save/delete for one application (users/{uid}/emails/{id}). */
export function useApplicationEditor(item: ApiItem, status: Status, onUpdate: () => void) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [company, setCompany] = useState(item.company);
  const [role, setRole] = useState(item.role || "");
  const [date, setDate] = useState(() => normalizeDateInput(item.date));
  const [editStatus, setEditStatus] = useState<Status>(status);
  const [notes, setNotes] = useState(item.notes || "");
  const [season, setSeason] = useState(item.szn || "");
  const [location, setLocation] = useState(item.lctn || "");
  // OA due date or interview date (and optional time), for OA / Interview cards
  const [deadline, setDeadline] = useState(() => deadlineFields(item, status));

  // The deadline fields follow the stage: switching OA -> Interview starts the interview date fresh
  const changeStatus = (next: Status) => {
    setEditStatus(next);
    setDeadline(deadlineFields(item, next));
  };

  const reset = () => {
    setCompany(item.company);
    setRole(item.role || "");
    setDate(normalizeDateInput(item.date));
    setEditStatus(status);
    setNotes(item.notes || "");
    setSeason(item.szn || "");
    setLocation(item.lctn || "");
    setDeadline(deadlineFields(item, status));
  };

  // Pick up fresh data while not editing
  useEffect(() => {
    if (!isEditing) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, status, isEditing]);

  const save = async () => {
    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("Please log in to edit");
        return;
      }
      const update: Record<string, any> = {
        company: company.trim(),
        role: role.trim(),
        date,
        label: editStatus,
        notes: notes.trim(),
        szn: season.trim(),
        lctn: location.trim(),
      };
      // Only when it was changed, so saving other fields leaves a synced deadline synced
      const before = deadlineFields(item, editStatus);
      if ((editStatus === "oa" || editStatus === "interview") && (deadline.date !== before.date || deadline.time !== before.time)) {
        update.deadline = deadline.date ? manualDeadline(editStatus, deadline.date, deadline.time) : null;
      }
      if (editStatus !== status) {
        update.history = appendStage(item.history ?? seedHistory(item), editStatus, "manual");
      }
      await updateDoc(doc(db, "users", user.uid, "emails", item.id), update);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error("Error updating application:", error);
      alert("Failed to update application");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    reset();
    setIsEditing(false);
  };

  const remove = async () => {
    try {
      setDeleting(true);
      const user = auth.currentUser;
      if (!user) {
        alert("Please log in to delete");
        return;
      }
      await deleteDoc(doc(db, "users", user.uid, "emails", item.id));
      onUpdate();
      setShowDelete(false);
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete application");
    } finally {
      setDeleting(false);
    }
  };

  return {
    isEditing,
    startEdit: () => setIsEditing(true),
    save,
    cancel,
    saving,
    showDelete,
    setShowDelete,
    remove,
    deleting,
    fields: { company, role, date, status: editStatus, notes, season, location, deadline },
    set: {
      company: setCompany,
      role: setRole,
      date: setDate,
      status: changeStatus,
      notes: setNotes,
      season: setSeason,
      location: setLocation,
      deadline: setDeadline,
    },
  };
}

// ---------------------------------------------------------------- menu & dialog

/** Row style for the melt dropdown menus (card menu, archive menu). */
export const MENU_ITEM = "rounded-[7px] px-2.5 py-2 text-[13px] font-medium text-melt-text1 focus:bg-melt-text1/[.06] [&_svg]:text-melt-text3";

/** The ⋯ menu on a card or row: Edit, Interview prep (soon), Delete. */
export function ApplicationMenu({
  company,
  onEdit,
  onDelete,
  triggerClassName,
}: {
  company: string;
  onEdit: () => void;
  onDelete: () => void;
  triggerClassName?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Options for ${company}`}
          className={cn(
            "grid h-6 w-6 flex-none place-items-center rounded-[7px] text-melt-text3 outline-none hover:bg-melt-text1/[.07] hover:text-melt-text1 focus-visible:ring-2 focus-visible:ring-melt-caramel/30 data-[state=open]:bg-melt-text1/[.07]",
            triggerClassName
          )}
        >
          <MoreVertical size={15} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        <DropdownMenuItem onSelect={onEdit} className={MENU_ITEM}>
          <Pencil size={15} strokeWidth={1.75} />
          Edit
        </DropdownMenuItem>
        {/* No interview prep page yet */}
        <DropdownMenuItem disabled className={MENU_ITEM}>
          <GraduationCap size={15} strokeWidth={1.75} />
          <span className="whitespace-nowrap">Interview prep</span>
          <span className="ml-auto font-plex text-[10.5px] text-melt-text3">Soon</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-melt-hair" />
        <DropdownMenuItem
          onSelect={onDelete}
          className={cn(MENU_ITEM, "text-[#A5492E] focus:bg-[#A5492E]/[.07] [&_svg]:text-[#A5492E]")}
        >
          <Trash2 size={15} strokeWidth={1.75} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DeleteApplicationDialog({
  open,
  onOpenChange,
  company,
  onConfirm,
  deleting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: string;
  onConfirm: () => void;
  deleting: boolean;
}) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
        <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">Delete application?</DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
          <span className="font-medium text-melt-text1">{company}</span> will be removed from your tracker. This can’t be
          undone.
        </DialogDescription>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} disabled={deleting} className={MELT_BUTTON_QUIET}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex h-11 items-center rounded-xl bg-[#A5492E] px-5 text-[15px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- move

/** Move an application to another stage (dragging a card to another column). */
export async function moveApplication(item: ApiItem, status: Status) {
  const user = auth.currentUser;
  if (!user) throw new Error("Please log in to move applications");
  await updateDoc(doc(db, "users", user.uid, "emails", item.id), {
    label: status,
    history: appendStage(item.history ?? seedHistory(item), status, "manual"),
  });
}

/** Undo a move: the stage and history the card had before it. */
export async function restoreApplication(item: ApiItem) {
  const user = auth.currentUser;
  if (!user) throw new Error("Please log in to move applications");
  await updateDoc(doc(db, "users", user.uid, "emails", item.id), {
    label: item.predicted_label,
    history: item.history?.length ? item.history : deleteField(),
  });
}

// ---------------------------------------------------------------- add

/** Fields for a new application, started from a column's or section's + button. */
export function useNewApplication(initialStatus: Status, onAdded: () => void, onClose: () => void) {
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [date, setDate] = useState(() => localDate());
  const [status, setStatus] = useState<Status>(initialStatus);
  const [notes, setNotes] = useState("");
  const [season, setSeason] = useState("");
  const [location, setLocation] = useState("");
  const [deadline, setDeadline] = useState({ date: "", time: "" });

  const canSave = Boolean(company.trim() && role.trim());
  const isEmpty = !company.trim() && !role.trim() && !notes.trim() && !season.trim() && !location.trim() && !deadline.date;

  const save = async () => {
    if (!canSave || saving) return;
    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to add applications");
      return;
    }
    setSaving(true);
    try {
      const id = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      const data: Record<string, any> = {
        message_id: id,
        company: company.trim(),
        role: role.trim(),
        label: status,
        score: 1.0,
        date,
        body: "Manually added application",
        notes: notes.trim(),
        szn: season.trim(),
        lctn: location.trim(),
        created_at: serverTimestamp(),
        manual_entry: true,
        archiveId: null,
        history: [{ stage: status, at: new Date(date || Date.now()).toISOString(), source: "seed" }],
      };
      if ((status === "oa" || status === "interview") && deadline.date) {
        data.deadline = manualDeadline(status, deadline.date, deadline.time);
      }
      await setDoc(doc(db, "users", user.uid, "emails", id), data);
      onAdded();
      onClose();
    } catch (error) {
      console.error("Error adding application:", error);
      alert("Failed to add application");
    } finally {
      setSaving(false);
    }
  };

  return {
    save,
    cancel: onClose,
    saving,
    canSave,
    isEmpty,
    saveLabel: "Add",
    fields: { company, role, date, status, notes, season, location, deadline },
    set: {
      company: setCompany,
      role: setRole,
      date: setDate,
      status: (next: Status) => {
        setStatus(next);
        setDeadline({ date: "", time: "" });
      },
      notes: setNotes,
      season: setSeason,
      location: setLocation,
      deadline: setDeadline,
    },
  };
}

/**
 * A blank card at the top of a column (or list section) to add an application
 * in place. Enter or Add saves. Clicking outside saves it once company and role
 * are filled, closes it while it's still empty, and otherwise leaves it open.
 * Esc closes it.
 */
export function NewApplicationCard({
  status,
  onAdded,
  onClose,
  wide = false,
  className,
}: {
  status: Status;
  onAdded: () => void;
  onClose: () => void;
  wide?: boolean;
  className?: string;
}) {
  const editor = useNewApplication(status, onAdded, onClose);
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(editor);
  latest.current = editor;

  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target || ref.current?.contains(target)) return;
      // The date picker and stage menu open in portals outside the card
      if (target.closest("[data-radix-popper-content-wrapper], [role='dialog'], [role='listbox']")) return;
      const { isEmpty, canSave, save, cancel } = latest.current;
      if (isEmpty) cancel();
      else if (canSave) save();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Esc inside an open picker closes just the picker
      if (e.key === "Escape" && !document.querySelector("[data-radix-popper-content-wrapper]")) latest.current.cancel();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.target as Element).tagName === "INPUT") {
          e.preventDefault();
          editor.save();
        }
      }}
      className={cn(
        "rounded-[14px] border border-melt-caramel/40 bg-melt-paper p-3.5 font-grotesk text-melt-text1 shadow-[0_12px_24px_-18px_rgba(92,52,23,.45)]",
        className
      )}
    >
      <ApplicationEditForm editor={editor} wide={wide} autoFocus />
    </div>
  );
}

// ---------------------------------------------------------------- edit form

type Editor = Pick<ReturnType<typeof useApplicationEditor>, "fields" | "set" | "save" | "cancel" | "saving"> & {
  /** Save button text; "Save" when left out */
  saveLabel?: string;
  /** False keeps the save button disabled */
  canSave?: boolean;
};

/** Inline edit form. `wide` spreads the fields out for a list row. */
export function ApplicationEditForm({ editor, wide = false, autoFocus = false }: { editor: Editor; wide?: boolean; autoFocus?: boolean }) {
  const { fields: f, set } = editor;
  return (
    <div className={cn("grid gap-2 font-grotesk", wide ? "grid-cols-6" : "grid-cols-2")}>
      <input
        value={f.company}
        onChange={(e) => set.company(e.target.value)}
        placeholder="Company"
        aria-label="Company"
        autoFocus={autoFocus}
        className={cn(EDIT_INPUT, "font-medium", wide ? "col-span-2" : "col-span-2")}
      />
      <input
        value={f.role}
        onChange={(e) => set.role(e.target.value)}
        placeholder="Role"
        aria-label="Role"
        className={cn(EDIT_INPUT, wide ? "col-span-4" : "col-span-2")}
      />
      <input
        value={f.season}
        onChange={(e) => set.season(e.target.value)}
        placeholder="Season, e.g. Summer 2027"
        aria-label="Season"
        className={cn(EDIT_INPUT, wide && "col-span-2")}
      />
      <input
        value={f.location}
        onChange={(e) => set.location(e.target.value)}
        placeholder="Location"
        aria-label="Location"
        className={cn(EDIT_INPUT, wide && "col-span-2")}
      />
      <DatePicker value={f.date} onChange={set.date} aria-label="Date applied" className={EDIT_INPUT} />
      <Select value={f.status} onValueChange={(v) => set.status(v as Status)}>
        <SelectTrigger aria-label="Stage" className="h-auto rounded-lg border-melt-hair px-2.5 py-1.5 text-[13px] text-melt-text1 focus:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="font-grotesk">
          {STATUS_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {(f.status === "oa" || f.status === "interview") && (
        <>
          <DatePicker
            value={f.deadline.date}
            onChange={(date) => set.deadline({ date, time: date ? f.deadline.time : "" })}
            placeholder={f.status === "oa" ? "OA due" : "Interview on"}
            aria-label={f.status === "oa" ? "OA due date" : "Interview date"}
            className={cn(EDIT_INPUT, wide && "col-span-2")}
          />
          <input
            type="time"
            value={f.deadline.time}
            onChange={(e) => set.deadline({ ...f.deadline, time: e.target.value })}
            disabled={!f.deadline.date}
            aria-label={f.status === "oa" ? "OA due time (optional)" : "Interview time (optional)"}
            title="Time (optional)"
            className={cn(EDIT_INPUT, "disabled:opacity-50", wide && "col-span-1")}
          />
        </>
      )}
      <textarea
        value={f.notes}
        onChange={(e) => set.notes(e.target.value)}
        placeholder="Notes"
        aria-label="Notes"
        rows={2}
        className={cn(EDIT_INPUT, "resize-none", wide ? "col-span-6" : "col-span-2")}
      />
      <div className={cn("mt-1 flex justify-end gap-2", wide ? "col-span-6" : "col-span-2")}>
        <button
          type="button"
          onClick={editor.cancel}
          disabled={editor.saving}
          className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-melt-text2 hover:bg-melt-text1/5 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={editor.save}
          disabled={editor.saving || editor.canSave === false}
          className="rounded-lg bg-melt-caramel px-3 py-1.5 text-[13px] font-medium text-white hover:bg-melt-press disabled:opacity-50"
        >
          {editor.saving ? "Saving…" : editor.saveLabel ?? "Save"}
        </button>
      </div>
    </div>
  );
}
